import React from "react";
import { Button, Modal } from "react-bootstrap";
import { observer, inject } from "mobx-react";

import toastService from "../../../services/ToastService";
import { DataSource } from "./DataSource";
import { FieldsToShow } from "./FieldsToShow";
import { Filter } from "./Filter";
import { CubeLinks } from "./CubeLinks";
import { CubeRelationships } from "./CubeRelationships";

import "./CubeQueryWizard.scss";

/**
 * @typedef {import("../../../stores/CubeQueryWizard/CubeQueryWizardStore").default} CubeQueryWizardStore
 */

@inject("cubeQueryWizardStore")
@observer
export class CubeQueryWizard extends React.Component {

  componentDidMount() {
    const { props } = this
    props.cubeQueryWizardStore.initializeData(
      props.cubeRootItem,
      props.cubeDetails,
      props.dataSetDistributions,
      props.hasCombinations,
      props.otherInformation
    )
  }

  componentDidUpdate(prevProps) {
    const cubeRootItem = this.props.cubeRootItem;
    const cubeDetails = this.props.cubeDetails;

    if (
      ((cubeRootItem && cubeRootItem !== prevProps.cubeRootItem) ||
        (cubeDetails && cubeDetails !== prevProps.cubeDetails)) &&
      this.props.isTemplateHierarchy
    ) {
      this.props.cubeQueryWizardStore.resetStateForTemplateHierarchie();
      this.props.cubeQueryWizardStore.initializeData(
        this.props.cubeRootItem,
        this.props.cubeDetails,
        this.props.dataSetDistributions,
        this.props.hasCombinations,
        this.props.otherInformation
      );
      this.props.cubeQueryWizardStore.isTemplateRepresentationSelectionOn = false;
    }
  }

  handleClose = () => {
    this.props.cubeQueryWizardStore.toggleShowQueryWizard(false);
  };

  handleAddTemplateRepresentationCombinationsClick = () => {
    this.props.cubeQueryWizardStore.toggleTemplateRepresentationSelection(true); // turn on selection
    this.handleClose();
  };

  handleChangeLimit = (event) => {
    const value = event.target.value;
    this.props.store.limit = value;
  };

  handleChangeSeriesKeysFilter = (event) => {
    const value = event.target.value;
    this.props.store.seriesKeysFilter = value;
  };

  copySqlToClipboard = () => {
    this.hiddenCodeTextArea.select();
    document.execCommand("copy");
    toastService.func_notify(
      "The SQL code has been copied to clipboard."
    );
  };

  componentWillUnmount() {
    this.props.cubeQueryWizardStore.resetState();
  }

  render() {
    const store = this.props.cubeQueryWizardStore;

    const sql = store.SQL;

    const { cubeDetails } = this.props;

    const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

    const whereTRItemsContainsItems = store.getwhereTRItemsContainsItems();
    return !IS_BIRD_APP && store.showQueryWizard ? (
      <Modal
        show={store.showQueryWizard}
        onHide={this.handleClose}
        dialogClassName="CubeQueryWizard"
        animation={false} // turning animation on causes quirk with drag-and-drop (both use css transform)
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <strong>Query Wizard</strong>
            <span className="CubeQueryWizard__ModalTitle-TonedDown">
              {" "}
              for {cubeDetails[0]?.name} ({cubeDetails[0]?.code})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DataSource
            store={store}
            onUpdateDataSource={this.handleUpdateDataSource}
            onUpdateForeignDataSource={this.handleUpdateForeignDataSource}
          />

          <hr />

          <FieldsToShow
            store={store}
            isRef={cubeDetails.maintenanceAgencyId === "ECB" ? true : false}
          />
          <hr />

          <Filter store={store} />

          <hr />

          {store.hasCombinations && !store.hasTemplateRepresentation && (
            <React.Fragment>
              <h4>Filter by Series Keys</h4>
              {store.filterBySeriesKeysDisabled && (
                <div className="alert alert-warning" role="alert">
                  <span
                    className="glyphicon glyphicon glyphicon-info-sign"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Info:</span>
                  Remove filters by attribute to enable filter by Series Keys
                </div>
              )}
              <div>
                <input
                  disabled={store.filterBySeriesKeysDisabled}
                  className="form-control"
                  type="text"
                  value={store.seriesKeysFilter}
                  onChange={this.handleChangeSeriesKeysFilter}
                />
              </div>
              <hr />
            </React.Fragment>
          )}

          {store.hasTemplateRepresentation && (
            <React.Fragment>
              <h4>Filter by data point</h4>
              <div>
                <Button
                  onClick={
                    this.handleAddTemplateRepresentationCombinationsClick
                  }
                  title="Query Wizard will be minimized, you can always
                  return back and continue."
                >
                  Select Data Points
                </Button>
              </div>
              {!whereTRItemsContainsItems && (
                <div className="CubeQueryWizard_data-source-row primary-cube">
                  <div className="alert alert-warning" role="alert">
                    <span
                      className="glyphicon glyphicon-info-sign"
                      aria-hidden="true"
                    ></span>
                    <span className="sr-only">Warning:</span> No data points
                    selected, please select data points.
                  </div>
                </div>
              )}

              <hr />
            </React.Fragment>
          )}

          <CubeLinks store={store} />

          <CubeRelationships store={store} />

          <h4>Show only the first records:</h4>
          <div>
            <input
              className="form-control"
              type="number"
              value={store.limit}
              onChange={this.handleChangeLimit}
            />
          </div>

          <hr />

          <h4>SQL query:</h4>
          <div className="CubeQueryWizard__sql-code">
            <Button
              onClick={this.copySqlToClipboard}
              bsClass="btn btn-default copy-to-clipboard-button"
            >
              Copy
            </Button>
            <pre
              ref={(el) => {
                this.sqlCodeRef = el;
              }}
            >
              {sql}
            </pre>
            {/* Hidden textarea, just for the sake of copying to clipboard: */}
            <textarea
              className="hidden-code-textarea"
              ref={(el) => {
                this.hiddenCodeTextArea = el;
              }}
              value={sql}
              readOnly={true}
            />
            <div>
              Paste your query in DEVO:{" "}
              <a
                href="https://devo.aws.ecb.de/hue"
                target="_blank"
                rel="noopener noreferrer"
              >
                HUE (ECB)
              </a>{" "}
              -{" "}
              <a
                href="https://hue-ncb.devo.escb.eu/"
                target="_blank"
                rel="noopener noreferrer"
              >
                HUE (NCBs)
              </a>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    ) : null;
  }
}
